import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { IMeetingNote } from '../../../interfaces/MeetingNotes';
import meetingService from '../../../services/meetingService';
import LoadingComponent from '../../common/loadingComponent';

import '../../../assets/styles/formattedTextViewer.css';

interface NoteCardProps {
  note: IMeetingNote;
  onClickEdit: (id: string) => void;
  onClickRemove: () => void;
}

interface PopoverButtonProps {
  label: string;
  onClick: () => void;
}

function PopoverButton({ label, onClick }: PopoverButtonProps) {
  return (
    <Button
      variant='text'
      sx={{
        padding: '6px 12px',
        justifyContent: 'flex-start',
        color: 'var(--Black, #181818)',
        '&:hover': {
          backgroundColor: (theme) => theme.palette.neutralBlackBg[100],
        },
        textTransform: 'none',
      }}
      onClick={onClick}
      fullWidth
    >
      {label}
    </Button>
  );
}

function NoteCard({ note, onClickEdit, onClickRemove }: NoteCardProps) {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [loading, setLoading] = useState(false);
  const timestamp = dayjs(note.created_at);

  const handleClick = async (id: string, type: 'edit' | 'remove') => {
    setLoading(true);
    setAnchorEl(null);
    if (type === 'edit') {
      onClickEdit(id);
    } else {
      await meetingService.deleteMeetingNote(note.id);
      onClickRemove();
    }
    setLoading(false);
  };

  return (
    <Stack
      sx={{
        position: 'relative',
        width: '100%',
        '& .tiptap': {
          padding: 'unset',
        },
      }}
    >
      {loading && (
        <Stack
          justifyContent='center'
          alignContent='center'
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <LoadingComponent />
        </Stack>
      )}
      <Stack direction='row' justifyContent='space-between'>
        <Box>
          {note.author}{' '}
          <Typography
            sx={{ color: (theme) => theme.palette.neutralBlackBg[600] }}
            component='span'
          >
            {timestamp.format('h:mm A')}
          </Typography>
        </Box>
        <Box>
          <IconButton
            disabled={loading}
            sx={{
              color: anchorEl
                ? 'var(--Black, #181818)'
                : (theme) => theme.palette.neutralBlackBg[600],
              '&:hover': {
                color: 'var(--Black, #181818)',
              },
            }}
            onClick={(event) => {
              setAnchorEl(event.currentTarget);
            }}
          >
            <MoreHorizIcon />
          </IconButton>
          <Popover
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={() => {
              setAnchorEl(null);
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              padding: '4px 0px',
            }}
          >
            <Stack width='100%' paddingY='4px'>
              <Box>
                <PopoverButton
                  label={intl.formatMessage({
                    defaultMessage: 'Edit',
                  })}
                  onClick={() => {
                    handleClick(note.id, 'edit');
                  }}
                />
              </Box>
              <Box>
                <PopoverButton
                  label={intl.formatMessage({
                    defaultMessage: 'Remove',
                  })}
                  onClick={() => {
                    handleClick(note.id, 'remove');
                  }}
                />
              </Box>
            </Stack>
          </Popover>
        </Box>
      </Stack>
      <Box>
        <Typography
          dangerouslySetInnerHTML={{ __html: note.description }}
          className={'formatted-text-viewer'}
        />
      </Box>
    </Stack>
  );
}

export default NoteCard;
